<template>
	<AppDropdown
		v-if="!isAllAssigneesShown"
		:popover-props="{
			placement: 'bottom-start',
			distance: dropdownDistance,
			skidding: -16,
			referenceNode: dropdownReference,
			triggers: [],
			shown: isDropdownOpen,
		}"
		@key.esc="closeDropdown"
	>
		<template #trigger>
			<SuiAvatar
				v-tooltip="showMoreLabel"
				:color="Constants.Colors.INACTIVE_COLOR"
				:initials="`+${extraCount}`"
				size="x-small"
				@click="openDropdown"
			/>
		</template>
		<template v-if="isDropdownOpen" #default>
			<div
				class="user-assign-select-header-expanded-list"
				:aria-label="expandedListAriaLabel"
			>
				<template v-for="item in rolesSorted" :key="item.id">
					<UserAssignSelectHeaderRoleItem
						v-if="item.type === RoleSlotType.Role"
						:role="item.role"
						:allocated-time="
							allocatedTimeInputs.roles[item.role.id] ?? ''
						"
						:has-individual-time-allocation="
							hasIndividualTimeAllocation
						"
						:has-custom-allocation="hasCustomAllocation"
						:confirmed-allocated-times="confirmedAllocatedTimes"
						:active-role-filter-id="activeRoleFilterId"
						@update:allocated-time="
							emit(
								'update:role-allocated-time',
								item.role.id,
								$event
							)
						"
						@focus="emit('focus', item.role.id)"
						@blur="emit('blur')"
						@update:active-role-filter-id="
							emit('update:active-role-filter-id', item.role.id)
						"
					/>
					<UserAssignSelectHeaderUserItem
						v-else
						:user="item.user"
						:availability="getUserAvailability(item.user.id)"
						:extra-used-availability="
							getExtraUsedAvailability(item.user.id)
						"
						:allocated-time="
							allocatedTimeInputs.users[item.user.id] ?? ''
						"
						:has-individual-time-allocation="
							hasIndividualTimeAllocation
						"
						:has-custom-allocation="hasCustomAllocation"
						:confirmed-allocated-times="confirmedAllocatedTimes"
						:active-role-filter-id="activeRoleFilterId"
						@update:allocated-time="
							emit(
								'update:user-allocated-time',
								item.user.id,
								$event
							)
						"
						@focus="emit('focus', item.user.id)"
						@blur="emit('blur')"
						@unselect="emit('unselect', item.user.id)"
					/>
				</template>
				<UserAssignSelectHeaderUserItem
					v-for="user in usersSorted"
					:key="user.id"
					:user="user"
					:availability="getUserAvailability(user.id)"
					:extra-used-availability="getExtraUsedAvailability(user.id)"
					:allocated-time="allocatedTimeInputs.users[user.id] ?? ''"
					:has-individual-time-allocation="
						hasIndividualTimeAllocation
					"
					:has-custom-allocation="hasCustomAllocation"
					:confirmed-allocated-times="confirmedAllocatedTimes"
					:active-role-filter-id="activeRoleFilterId"
					@update:allocated-time="
						emit('update:user-allocated-time', user.id, $event)
					"
					@focus="emit('focus', user.id)"
					@blur="emit('blur')"
					@unselect="emit('unselect', user.id)"
				/>
				<button
					v-tooltip="collapseLabel"
					type="button"
					class="user-assign-select-header-expanded-list__collapse-button"
					:aria-label="collapseLabel"
					@click="closeDropdown"
				>
					<SuiIcon
						name="icon-arrow-up-filled-16"
						:fill="Constants.Colors.INACTIVE_COLOR"
					/>
				</button>
			</div>
		</template>
	</AppDropdown>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { SuiAvatar, SuiIcon } from '@developers/scoro-ui';
import type {
	User,
	UserAssignSelectUserFragment,
	UserAssignSelectAvailabilityQuery,
} from '@/generated/graphql';
import type {
	TaskAssigneeInputDuration,
	TaskAssigneeAllocatedDuration,
} from '@/features/Task/components/TaskUsers/types';
import { getDurationInSeconds } from '@/utils/formatting';
import { Constants } from '@/constants';
import AppDropdown from '@/features/App/AppDropdown/AppDropdown.vue';
import UserAssignSelectHeaderUserItem from './UserAssignSelectHeaderUserItem.vue';
import UserAssignSelectHeaderRoleItem from './UserAssignSelectHeaderRoleItem.vue';
import {
	RoleSlotType,
	type UserAssignSelectRole,
	type RoleSlotItem,
} from '../../../types';

export interface UserAssignSelectHeaderExpandedListProps {
	rolesSorted: RoleSlotItem[];
	usersSorted: UserAssignSelectUserFragment[];
	allocatedTimeInputs: TaskAssigneeInputDuration;
	hasIndividualTimeAllocation: boolean;
	hasCustomAllocation: boolean;
	confirmedAllocatedTimes?: TaskAssigneeAllocatedDuration;
	activeRoleFilterId: UserAssignSelectRole['id'] | null;
	taskUsersAvailability?: UserAssignSelectAvailabilityQuery['TaskUsersAvailability'];
	extraCount: number;
	isAllAssigneesShown: boolean;
	dropdownReference: () => Element | null;
	usersListElement: HTMLDivElement | null;
	showMoreLabel: string;
	expandedListAriaLabel: string;
	collapseLabel: string;
}

const {
	rolesSorted,
	usersSorted,
	allocatedTimeInputs,
	hasIndividualTimeAllocation,
	hasCustomAllocation,
	confirmedAllocatedTimes,
	activeRoleFilterId,
	taskUsersAvailability = [],
	extraCount,
	isAllAssigneesShown,
	dropdownReference,
	usersListElement,
	showMoreLabel,
	expandedListAriaLabel,
	collapseLabel,
} = defineProps<UserAssignSelectHeaderExpandedListProps>();

const emit = defineEmits<{
	'update:role-allocated-time': [roleId: string, value: string];
	'update:user-allocated-time': [userId: string, value: string];
	focus: [id: string];
	blur: [];
	'update:active-role-filter-id': [roleId: UserAssignSelectRole['id']];
	unselect: [userId: User['id']];
}>();

const isDropdownOpen = ref(false);
const dropdownDistance = ref(0);

const openDropdown = () => {
	dropdownDistance.value = ((usersListElement?.clientHeight ?? 0) + 16) * -1;
	isDropdownOpen.value = true;
};

const closeDropdown = () => {
	isDropdownOpen.value = false;
};

const getUserAvailability = (userId: UserAssignSelectUserFragment['id']) => {
	return taskUsersAvailability?.find(
		(availability) => availability.user.id === userId
	);
};

const getExtraUsedAvailability = (
	userId: UserAssignSelectUserFragment['id']
) => {
	const allocatedTimeInSeconds = getDurationInSeconds(
		allocatedTimeInputs.users[userId] ?? '0'
	);
	const confirmedTime = confirmedAllocatedTimes?.users.get(userId) ?? 0;

	return allocatedTimeInSeconds - confirmedTime;
};
</script>

<style lang="scss" scoped>
.user-assign-select-header-expanded-list {
	padding: 2rem;
	width: 40rem;
	max-height: 30vh;
	overflow-y: auto;
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.5rem;

	&__collapse-button {
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 50%;
		width: 3rem;
		height: 3rem;
		background-color: $neutral-20;

		&:hover {
			background-color: $neutral-40;
		}
	}
}
</style>
